import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import PilihFA from "../components/etams/PilihFA";
import { apiErrorMessage } from "../lib/inventoryApi";
import { tampilHp } from "../lib/nomorHp";

const STEPS = ["Customer", "Produk & Jumlah", "Catatan", "Periksa"];
const LABEL_PRODUK = { ATASAN_ONLY: "Atasan Saja", SET_ATASAN_CELANA: "Set Atasan + Celana" };
const LABEL_PAKET = { REGULER: "Reguler", EXPRESS: "Express", SUPER_EXPRESS: "Super Express" };

export default function DesignRequestCreate() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [step, setStep] = useState(0);
  const [faLama, setFaLama] = useState(null);   // C20: FA lama terpilih (PilihFA)
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [customerQuery, setCustomerQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  // T3: pesanan yang masih berjalan atas nomor HP customer terpilih (cegah PD dobel AI vs staf).
  const [berjalan, setBerjalan] = useState([]);
  // T1: cabang PD bila cabang asal customer tidak terbaca (dan akun tidak terikat cabang).
  const [stores, setStores] = useState([]);
  const [cabangPd, setCabangPd] = useState("");
  const cabangTerbaca = customer?.store_origin_id || profile?.store_id || "";
  useEffect(() => {
    setBerjalan([]);
    if (!customer?.id) return;
    let batal = false;
    api.get(`/customers/${customer.id}/pesanan-berjalan`)
      .then((r) => { if (!batal) setBerjalan(r.data?.items || []); })
      .catch(() => { /* peringatan saja — tidak menahan pembuatan PD */ });
    return () => { batal = true; };
  }, [customer?.id]);
  useEffect(() => {
    if (!customer || cabangTerbaca || stores.length) return;
    api.get("/stores").then((r) => setStores(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat.")));
  }, [customer, cabangTerbaca, stores.length]);

  const [form, setForm] = useState({
    product_summary_type: "ATASAN_ONLY",
    package_type: "REGULER",
    is_repeat_order: false,
    parent_fa_id: "",
    preliminary_qty_atasan: 6,
    preliminary_qty_celana: 0,
    notes: "",
  });

  // `?customer_id=` dari detail customer (tombol "Buat PD Baru" — dulu menuju
  // `/fa/new` yang nol ada; FA lahir dari PD sejak K35/K55, jadi pintunya PD):
  // customer langsung terpilih, pencarian dilewati.
  const [searchParams] = useSearchParams();
  const customerIdAwal = searchParams.get("customer_id");
  useEffect(() => {
    if (!customerIdAwal) return;
    let batal = false;
    api.get(`/customers/${customerIdAwal}`)
      .then((r) => { if (!batal && r.data?.id) { setCustomer(r.data); setCustomers([r.data]); } })
      .catch(() => toast.error("Customer dari tautan tidak ditemukan — cari manual."));
    return () => { batal = true; };
  }, [customerIdAwal]);

  async function searchCustomers() {
    try {
      const r = await api.get("/customers", {
        params: { q: customerQuery, limit: 20 },
      });
      setCustomers(r.data?.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mencari customer"));
    }
  }

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    setSubmitting(true);
    setApiError(null);
    try {
      const body = {
        customer_id: customer.id,
        store_id: cabangTerbaca || cabangPd,
        product_summary_type: form.product_summary_type,
        package_type: form.package_type,
        is_repeat_order: form.is_repeat_order,
        parent_fa_id: form.is_repeat_order ? form.parent_fa_id || null : null,
        preliminary_qty_atasan: Number(form.preliminary_qty_atasan),
        preliminary_qty_celana:
          form.product_summary_type === "SET_ATASAN_CELANA"
            ? Number(form.preliminary_qty_celana)
            : 0,
        notes: form.notes || null,
      };
      const r = await api.post("/design-requests", body);
      toast.success(`DR ${r.data.dr_number} berhasil dibuat`);
      navigate(`/design-requests/${r.data.id}`);
    } catch (e) {
      setApiError(apiErrorMessage(e, "Permintaan desain gagal dibuat."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="dr-create-page">
      <h1 className="text-2xl font-bold mb-2">Buat Permintaan Desain</h1>
      <div className="flex gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`px-3 py-1 rounded-full text-xs border ${
              i === step
                ? "bg-primary text-primary-foreground border-primary"
                : i < step
                ? "bg-success-soft text-success-soft-foreground border-success-soft-border"
                : "bg-card text-muted-foreground border-border"
            }`}
          >
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          {step === 0 && (
            <>
              <Label>Cari Customer</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="customer-search"
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Nama / telepon / email"
                  onKeyDown={(e) => e.key === "Enter" && searchCustomers()}
                />
                <Button data-testid="customer-search-btn" onClick={searchCustomers}>
                  Cari
                </Button>
                <Button
                  data-testid="customer-new-btn"
                  variant="outline"
                  onClick={() => navigate("/customers?new=1")}
                >
                  + Customer Baru
                </Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-auto">
                {customers.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setCustomer(c)}
                    data-testid={`customer-pick-${c.id}`}
                    className={`p-3 border rounded cursor-pointer ${
                      customer?.id === c.id
                        ? "border-primary bg-muted/50"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {tampilHp(c.phone) || "tanpa nomor"} · {c.email || "tanpa email"}
                    </div>
                  </div>
                ))}
                {customers.length === 0 && customerQuery && (
                  <div className="text-sm text-muted-foreground">Tidak ada hasil.</div>
                )}
              </div>
              <PeringatanBerjalan items={berjalan} />
              {customer && !cabangTerbaca && (
                <div className="space-y-1.5" data-testid="dr-pilih-cabang">
                  <Label>Cabang pesanan</Label>
                  <p className="text-xs text-muted-foreground">
                    Cabang asal customer ini belum tercatat. Pilih cabang yang menangani pesanan ini.
                  </p>
                  <Select value={cabangPd} onValueChange={setCabangPd}>
                    <SelectTrigger data-testid="dr-store-select"><SelectValue placeholder="Pilih cabang..." /></SelectTrigger>
                    <SelectContent>
                      {stores.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <Label>Tipe Produk</Label>
                <Select
                  value={form.product_summary_type}
                  onValueChange={(v) => update("product_summary_type", v)}
                >
                  <SelectTrigger data-testid="product-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ATASAN_ONLY">Atasan Saja</SelectItem>
                    <SelectItem value="SET_ATASAN_CELANA">Set Atasan + Celana</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Paket Waktu</Label>
                <Select value={form.package_type} onValueChange={(v) => update("package_type", v)}>
                  <SelectTrigger data-testid="package-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REGULER">Reguler (14 hari kerja)</SelectItem>
                    <SelectItem value="EXPRESS">Express (7 hari kerja)</SelectItem>
                    <SelectItem value="SUPER_EXPRESS">Super Express (3 hari kerja)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="is-repeat"
                  type="checkbox"
                  checked={form.is_repeat_order}
                  onChange={(e) => update("is_repeat_order", e.target.checked)}
                />
                <Label>Repeat order (pakai desain lama)</Label>
              </div>
              {form.is_repeat_order && (
                <div>
                  <Label>FA lama (untuk repeat)</Label>
                  {/* Inventaris C20: cari FA lama lewat nomor/nama — dulu UUID diketik tangan. */}
                  <PilihFA
                    value={faLama}
                    denganWo={false}
                    testId="parent-fa"
                    onChange={(v) => { setFaLama(v); update("parent_fa_id", v?.fa?.id || ""); }}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Jumlah Atasan</Label>
                  <Input
                    data-testid="qty-atasan"
                    type="number"
                    min="1"
                    value={form.preliminary_qty_atasan}
                    onChange={(e) => update("preliminary_qty_atasan", e.target.value)}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Minimal 6 (pesanan baru) / 1 (repeat dari FA lama)
                  </div>
                </div>
                {form.product_summary_type === "SET_ATASAN_CELANA" && (
                  <div>
                    <Label>Jumlah Celana</Label>
                    <Input
                      data-testid="qty-celana"
                      type="number"
                      min="1"
                      value={form.preliminary_qty_celana}
                      onChange={(e) => update("preliminary_qty_celana", e.target.value)}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Label>Catatan / Preferensi Desain (opsional)</Label>
              <Textarea
                data-testid="notes"
                rows={6}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Warna dominan, gaya, referensi..."
              />
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-2 text-sm">
                <div><strong>Customer:</strong> {customer?.name}</div>
                <PeringatanBerjalan items={berjalan} />
                <div><strong>Tipe Produk:</strong> {LABEL_PRODUK[form.product_summary_type] || "—"}</div>
                <div><strong>Paket Waktu:</strong> {LABEL_PAKET[form.package_type] || "—"}</div>
                <div><strong>Repeat Order:</strong> {form.is_repeat_order ? "Ya" : "Tidak"}</div>
                <div><strong>Jumlah Atasan:</strong> {form.preliminary_qty_atasan}</div>
                {form.product_summary_type === "SET_ATASAN_CELANA" && (
                  <div><strong>Jumlah Celana:</strong> {form.preliminary_qty_celana}</div>
                )}
                {form.notes && <div><strong>Catatan:</strong> {form.notes}</div>}
              </div>

              {apiError && (
                <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm" data-testid="api-error">
                  <div className="text-danger-soft-foreground whitespace-pre-wrap">{apiError}</div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between mt-4">
        <Button
          data-testid="step-back"
          variant="outline"
          disabled={step === 0}
          onClick={() => setStep(step - 1)}
        >
          Kembali
        </Button>
        {step < 3 ? (
          <Button
            data-testid="step-next"
            disabled={step === 0 && (!customer || !(cabangTerbaca || cabangPd))}
            onClick={() => setStep(step + 1)}
          >
            Lanjut
          </Button>
        ) : (
          <Button
            data-testid="dr-submit"
            disabled={submitting || !customer}
            onClick={submit}
          >
            {submitting ? "Mengirim..." : "Kirim Permintaan Desain"}
          </Button>
        )}
      </div>
    </div>
  );
}

/* T3 (Titipan auditor 26 Okt 2026): peringatan — bukan penghalang. Pesan ulang sah; yang dicegah PD dobel
   untuk pesanan yang sama (mis. AI WhatsApp sudah membuatkan PD, lalu staf membuat lagi). */
function PeringatanBerjalan({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="rounded-md border border-warning-soft-border bg-warning-soft p-3 text-sm text-warning-soft-foreground"
         data-testid="dr-peringatan-berjalan">
      <div className="font-medium">Nomor HP customer ini masih punya pesanan berjalan:</div>
      <ul className="list-disc list-inside mt-1">
        {items.map((x) => (
          <li key={x.dr_id}>
            {x.fa_number || x.dr_number}
            {x.customer_lain ? " (tercatat atas nama data customer lain dengan nomor yang sama)" : ""}
          </li>
        ))}
      </ul>
      <div className="mt-1">Pastikan ini memang pesanan baru, bukan pesanan yang sama dibuat dua kali.</div>
    </div>
  );
}
